/**
 * @fileoverview Admin notification email template editor (MOR-716).
 *
 * Template list on the left, @react-email/editor body editor + subject input
 * on the right, with a {{variable}} palette, live sample preview, test-send
 * and reset-to-default. Saved HTML keeps {{key}} placeholders intact —
 * they're substituted at send time (MOR-704 contract).
 */

import { useMemo, useRef, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { EmailEditor, type EmailEditorRef } from "@react-email/editor";
import "@react-email/editor/themes/default.css";
import { Copy, RotateCcw, Save, Send } from "lucide-react";

import { api } from "@cvx/_generated/api";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Spinner } from "@/ui/primitives/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/ui/composed/sections/tabs";
import { cn } from "@/utils/misc";
import { toast } from "sonner";

type TemplateKey = "task_assigned" | "task_activity";

/** Sample values mirrored from the backend's test-send substitution. */
const PREVIEW_VARS: Record<string, string> = {
  recipientFirstName: "Alex",
  actorName: "Jordan Lee",
  taskTitle: "Lodge Form 505",
  jobName: "Acme Holdings Pty Ltd",
  taskUrl: "#",
  settingsUrl: "#",
  changesHtml:
    '<ul style="list-style:none;margin:0;padding:0;">' +
    '<li style="margin:0 0 10px 0;color:#1f2937;font-size:16px;line-height:1.6;"><span style="color:#166534;font-weight:600;">&rarr;&nbsp;&nbsp;</span>Status changed&nbsp; <span style="display:inline-block;background:#f3f4f6;color:#374151;border-radius:6px;padding:2px 8px;font-size:13px;font-weight:600;">To Do</span> <span style="color:#8b929b;">&rarr;</span> <span style="display:inline-block;background:#fef3c7;color:#b45309;border-radius:6px;padding:2px 8px;font-size:13px;font-weight:600;">In Progress</span><span style="color:#8b929b;"> — Jordan Lee</span></li>' +
    '<li style="margin:0 0 10px 0;color:#1f2937;font-size:16px;line-height:1.6;"><span style="color:#166534;font-weight:600;">&rarr;&nbsp;&nbsp;</span>New comment<span style="color:#8b929b;"> — Sam Patel</span></li>' +
    "</ul>",
};

function substitutePreview(template: string): string {
  return template.replace(
    /\{\{(\w+)\}\}/g,
    (_m, key: string) => PREVIEW_VARS[key] ?? "",
  );
}

export function EmailTemplateEditorPage() {
  const templates = useQuery(api.notifications.emailTemplates.listAll);
  const save = useMutation(api.notifications.emailTemplates.save);
  const reset = useMutation(api.notifications.emailTemplates.resetToDefault);
  const sendTest = useAction(api.notifications.emailTemplates.sendTest);

  const [selectedKey, setSelectedKey] = useState<TemplateKey>("task_assigned");
  const [subject, setSubject] = useState<string | null>(null);
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [dirty, setDirty] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const editorRef = useRef<EmailEditorRef | null>(null);

  const selected = useMemo(
    () => templates?.find((t) => t.key === selectedKey),
    [templates, selectedKey],
  );

  if (!templates || !selected) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  const effectiveSubject = subject ?? selected.subject;

  const selectTemplate = (key: TemplateKey) => {
    setSelectedKey(key);
    setSubject(null);
    setDirty(false);
    setMode("edit");
  };

  const handleSave = async () => {
    const ref = editorRef.current;
    if (!ref) return;
    const html = await ref.getEmailHTML();
    await save({
      key: selectedKey,
      subject: effectiveSubject,
      html,
      editorState: JSON.stringify(ref.getJSON()),
    });
    setDirty(false);
    toast.success("Template saved");
  };

  const handleReset = async () => {
    await reset({ key: selectedKey });
    setSubject(null);
    setDirty(false);
    setResetOpen(false);
    toast.success("Template reset to default");
  };

  const handleSendTest = async () => {
    if (dirty) await handleSave();
    await sendTest({ key: selectedKey });
    toast.success("Test email sent to you");
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Email Templates</h1>
        <p className="text-sm text-muted-foreground">
          The emails Morgan sends for task notifications. Variables like{" "}
          <code className="rounded bg-muted px-1">{"{{taskTitle}}"}</code> are
          filled in at send time.
        </p>
      </div>

      <div className="flex gap-6">
        {/* Template list */}
        <div className="flex w-64 shrink-0 flex-col gap-2">
          {templates.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => selectTemplate(t.key)}
              className={cn(
                "rounded-lg border p-3 text-left transition-colors",
                t.key === selectedKey
                  ? "border-primary bg-card"
                  : "bg-card/50 hover:bg-card",
              )}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{t.name}</span>
                {t.isCustomised && (
                  <Badge variant="secondary" className="text-[10px]">
                    Customised
                  </Badge>
                )}
              </div>
              <p className="pt-1 text-xs text-muted-foreground">
                {t.description}
              </p>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <Tabs
              value={mode}
              onValueChange={(v) => setMode(v as "edit" | "preview")}
            >
              <TabsList>
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetOpen(true)}
                disabled={!selected.isCustomised}
              >
                <RotateCcw className="size-3.5" />
                Reset to default
              </Button>
              <Button variant="outline" size="sm" onClick={handleSendTest}>
                <Send className="size-3.5" />
                Send test to me
              </Button>
              <Button size="sm" onClick={handleSave} disabled={!dirty}>
                <Save className="size-3.5" />
                Save
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="template-subject">Subject</Label>
            <Input
              id="template-subject"
              value={effectiveSubject}
              onChange={(e) => {
                setSubject(e.target.value);
                setDirty(true);
              }}
            />
          </div>

          <div className="flex flex-wrap gap-1.5">
            {selected.variables.map((v) => (
              <button
                key={v.key}
                type="button"
                title={`${v.description} — click to copy`}
                className="flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  void navigator.clipboard.writeText(`{{${v.key}}}`);
                  toast.success(`{{${v.key}}} copied`);
                }}
              >
                <Copy className="size-3" />
                {`{{${v.key}}}`}
              </button>
            ))}
          </div>

          {mode === "edit" ? (
            <div className="min-h-[28rem] rounded-lg border bg-card p-4">
              <EmailEditor
                key={`${selectedKey}-${selected.isCustomised}`}
                ref={editorRef}
                content={
                  selected.editorState
                    ? JSON.parse(selected.editorState)
                    : selected.html
                }
                placeholder="Write the email body…"
                onUpdate={() => setDirty(true)}
              />
            </div>
          ) : (
            <iframe
              title="Email preview"
              className="min-h-[28rem] rounded-lg border bg-white"
              sandbox=""
              srcDoc={substitutePreview(selected.html)}
            />
          )}
        </div>
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to default?</AlertDialogTitle>
            <AlertDialogDescription>
              Your customised version of "{selected.name}" will be discarded and
              the built-in template restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
